<template>
  <nav :class="navClasses" class="shadow-lg fixed top-0 left-0 right-0 z-50">
    <div class="nav-container">
      <div class="nav-content">
        <!-- Logo and Navigation -->
        <div class="flex items-center">
          <!-- Logo with Tagline -->
          <router-link :to="logoLink" @click="handleResetSearch" class="flex flex-col items-start flex-shrink-0">
            <!-- Zaveato: Text logo -->
            <span class="text-2xl sm:text-3xl font-normal tracking-tight text-white" :style="{ fontFamily: brand.logo.fontFamily }">
              {{ brand.logo.text }}<span class="text-rose-500">{{ brand.logo.accentLetter }}</span>{{ brand.logo.suffix }}<span class="text-rose-500">.</span>
            </span>
            <!-- Tagline (hidden on mobile) -->
            <span v-if="brand.tagline" class="hidden sm:block text-xs text-gray-300 tracking-wide font-medium" :style="{ fontFamily: brand.logo.fontFamily }">{{ brand.tagline }}</span>
          </router-link>

          <!-- Main Navigation -->
          <div class="nav-links">
            <!-- Zaveato Services Mode: Services-only nav -->
            <template v-if="isServicesMode && !authStore.hasRole('ServiceUser')">
              <router-link
                to="/services"
                @click="handleResetSearch"
                class="nav-link"
                :class="{ 'nav-link-active': $route.query.mode === 'services' || $route.path.startsWith('/services') }"
              >
                <i class="pi pi-list mr-2"></i>
                Browse Services
              </router-link>
            </template>

            <!-- Zaveato Shop Mode: Shop nav -->
            <template v-else-if="!authStore.hasRole('ServiceUser')">
              <router-link
                to="/"
                @click="handleResetSearch"
                class="nav-link"
                :class="{ 'nav-link-active': $route.path === '/' && !$route.query.mode }"
              >
                <i class="pi pi-shopping-bag mr-2"></i>
                Browse Shops
              </router-link>
            </template>

            <!-- Guest Appointment Lookup (show in services mode) -->
            <router-link
              v-if="!authStore.isAuthenticated && isServicesMode"
              to="/appointments/lookup"
              class="nav-link"
              :class="{ 'nav-link-active': $route.path === '/appointments/lookup' }"
            >
              <i class="pi pi-search mr-2"></i>
              Find Booking
            </router-link>

            <!-- Authenticated User Navigation -->
            <template v-if="authStore.isAuthenticated">
              <router-link
                v-if="!authStore.hasRole('Admin') && !authStore.hasRole('Provider') && !authStore.hasRole('ServiceUser')"
                to="/dashboard"
                class="nav-link"
                :class="{ 'nav-link-active': $route.path === '/dashboard' }"
              >
                <i class="pi pi-chart-line mr-2"></i>
                Dashboard
              </router-link>

              <router-link
                v-if="authStore.hasPermission('appointments.read') && !authStore.hasRole('Provider') && !authStore.hasRole('Admin')"
                to="/appointments"
                class="nav-link"
                :class="{ 'nav-link-active': $route.path.startsWith('/appointments') }"
              >
                <i class="pi pi-calendar mr-2"></i>
                Appointments
              </router-link>
            </template>


            <!-- Service User Dashboard (for authenticated service users) -->
            <router-link
              v-if="authStore.isAuthenticated && authStore.hasRole('ServiceUser')"
              to="/service-user/dashboard"
              class="nav-link"
              :class="{ 'nav-link-active': $route.path === '/service-user/dashboard' || $route.path === '/service-user' }"
            >
              <i class="pi pi-th-large mr-2"></i>
              My Services
            </router-link>

            <!-- Provider Hub (for authenticated providers) -->
            <router-link
              v-if="authStore.isAuthenticated && authStore.hasRole('Provider')"
              to="/provider"
              class="nav-link provider-hub-link"
              :class="{ 'provider-hub-link-active': $route.path.startsWith('/provider') }"
            >
              <i class="pi pi-briefcase mr-2"></i>
              Provider Hub
            </router-link>
            
            <!-- Admin Panel (for authenticated admins) -->
            <router-link
              v-if="authStore.isAuthenticated && authStore.hasRole('Admin')"
              to="/admin"
              class="nav-link admin-link"
              :class="{ 'nav-link-active': $route.path.startsWith('/admin') }"
            >
              <i class="pi pi-shield mr-2"></i>
              Admin Panel
            </router-link>
          </div>
        </div>


        <!-- Right Side -->
        <div class="nav-actions">
          <!-- Guest User Actions -->
          <div v-if="!authStore.isAuthenticated" class="hidden lg:flex items-center space-x-3">
            <router-link
              to="/how-it-works"
              class="text-sm font-medium text-white/90 hover:text-white px-3 py-2 rounded-md hover:bg-white/10"
            >
              <i class="pi pi-question-circle mr-1"></i>
              How It Works
            </router-link>
            <router-link
              to="/contact"
              class="text-sm font-medium text-white/90 hover:text-white px-3 py-2 rounded-md hover:bg-white/10 transition-colors duration-200"
            >
              <i class="pi pi-comments mr-1.5"></i>
              Let's Chat
            </router-link>
            <router-link
              to="/auth/register"
              class="text-sm font-semibold bg-white hover:bg-gray-100 px-4 py-2 rounded-lg shadow-sm transition-all duration-200 hover:shadow-md"
              :class="getStartedBtnClass"
            >
              Get Started
            </router-link>
            <router-link
              to="/auth/login"
              class="text-sm font-medium text-white hover:text-white px-3 py-2 rounded-md hover:bg-white/10"
            >
              Sign In
            </router-link>
          </div>

          <!-- Authenticated User Section -->
          <template v-else>
            <!-- Setup Guides for Providers -->
            <div v-if="authStore.hasRole('Provider')" class="relative hidden sm:block" ref="guidesDropdownRef">
              <button
                @click="showGuidesDropdown = !showGuidesDropdown"
                class="flex items-center gap-2 text-sm font-medium px-3 py-1.5 rounded-lg bg-gradient-to-r from-amber-500 to-orange-500 text-white hover:from-amber-600 hover:to-orange-600 transition-all shadow-md"
              >
                <i class="pi pi-book text-xs"></i>
                <span class="hidden md:inline">Setup Guides</span>
                <i class="pi pi-chevron-down text-[10px] transition-transform" :class="{ 'rotate-180': showGuidesDropdown }"></i>
              </button>
              <Transition name="dropdown">
                <div
                  v-if="showGuidesDropdown"
                  class="absolute right-0 mt-2 w-64 bg-white rounded-xl shadow-2xl border border-gray-100 py-2 z-50"
                >
                  <div class="px-4 py-2 border-b border-gray-100">
                    <p class="text-xs font-semibold text-gray-400 uppercase tracking-wider">Setup Manuals</p>
                  </div>
                  <a
                    href="/guide/service-setup"
                    target="_blank"
                    class="flex items-center gap-3 px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                    @click="showGuidesDropdown = false"
                  >
                    <div class="w-9 h-9 rounded-lg bg-primary-600 flex items-center justify-center">
                      <i class="pi pi-calendar text-white text-base"></i>
                    </div>
                    <div class="flex-1">
                      <div class="font-semibold text-gray-900">Service Setup</div>
                      <div class="text-xs text-gray-500">How to setup your service</div>
                    </div>
                    <i class="pi pi-arrow-up-right text-gray-400 text-xs"></i>
                  </a>
                  <a
                    href="/guide/shop-setup"
                    target="_blank"
                    class="flex items-center gap-3 px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                    @click="showGuidesDropdown = false"
                  >
                    <div class="w-9 h-9 rounded-lg bg-rose-600 flex items-center justify-center">
                      <i class="pi pi-shop text-white text-base"></i>
                    </div>
                    <div class="flex-1">
                      <div class="font-semibold text-gray-900">Shop Setup</div>
                      <div class="text-xs text-gray-500">How to setup your shop</div>
                    </div>
                    <i class="pi pi-arrow-up-right text-gray-400 text-xs"></i>
                  </a>
                </div>
              </Transition>
            </div>

            <!-- Contact -->
            <router-link
              to="/contact"
              class="hidden sm:flex items-center text-xs md:text-sm font-medium text-white/90 hover:text-white px-3 py-2 rounded-md hover:bg-white/10 transition-colors duration-200"
            >
              <i class="pi pi-comments mr-1.5"></i>
              Let's Chat
            </router-link>

            <!-- User Menu -->
            <div class="relative">
              <button
                @click="toggleUserMenu"
                class="flex items-center text-sm rounded-full focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-white/50"
              >
                <div class="h-8 w-8 rounded-full bg-white/20 flex items-center justify-center text-white font-medium">
                  {{ authStore.userInitials }}
                </div>
                <span class="ml-2 text-white font-medium hidden md:block">
                  {{ authStore.userName }}
                </span>
                <i class="pi pi-chevron-down ml-1 text-white/70"></i>
              </button>

              <!-- User Dropdown -->
              <div
                v-if="showUserMenu"
                class="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-50"
              >
                <router-link
                  v-if="!authStore.hasRole('Provider') && !authStore.hasRole('ShopStaff')"
                  to="/my-orders"
                  class="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  @click="showUserMenu = false"
                >
                  <i class="pi pi-shopping-bag mr-2"></i>
                  My Orders
                </router-link>
                <router-link
                  to="/profile"
                  class="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  @click="showUserMenu = false"
                >
                  <i class="pi pi-user mr-2"></i>
                  Your Profile
                </router-link>
                <router-link
                  to="/profile/security"
                  class="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  @click="showUserMenu = false"
                >
                  <i class="pi pi-shield mr-2"></i>
                  Security
                </router-link>
                <hr class="my-1">
                <button
                  @click="handleLogout"
                  class="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                >
                  <i class="pi pi-sign-out mr-2"></i>
                  Sign out
                </button>
              </div>
            </div>
          </template>

          <!-- Mobile menu button -->
          <button
            @click="toggleMobileMenu"
            class="mobile-menu-btn"
          >
            <i class="pi pi-bars text-lg"></i>
          </button>
        </div>
      </div>
    </div>

    <!-- Mobile Navigation -->
    <div v-if="showMobileMenu" class="mobile-nav">
      <div class="mobile-nav-content">
        <!-- Zaveato Services Mode: Services-only nav on mobile -->
        <template v-if="isServicesMode && !authStore.hasRole('ServiceUser')">
          <router-link
            to="/services"
            class="mobile-nav-link"
            @click="handleResetSearch(); showMobileMenu = false"
          >
            <i class="pi pi-list mr-2"></i>
            Browse Services
          </router-link>
        </template>

        <!-- Zaveato Shop Mode: Shop nav on mobile -->
        <template v-else-if="!authStore.hasRole('ServiceUser')">
          <router-link
            to="/"
            class="mobile-nav-link"
            @click="handleResetSearch(); showMobileMenu = false"
          >
            <i class="pi pi-shopping-bag mr-2"></i>
            Browse Shops
          </router-link>
        </template>

        <!-- Guest Options -->
        <template v-if="!authStore.isAuthenticated">
          <router-link
            v-if="isServicesMode"
            to="/appointments/lookup"
            class="mobile-nav-link"
            @click="showMobileMenu = false"
          >
            <i class="pi pi-search mr-2"></i>
            Find Booking
          </router-link>

          <router-link
            to="/contact"
            class="mobile-nav-link"
            @click="showMobileMenu = false"
          >
            <i class="pi pi-envelope mr-2"></i>
            Get In Touch
          </router-link>

          <div class="px-3 py-3 space-y-3 mt-2 border-t border-gray-200">
            <router-link
              to="/how-it-works"
              class="block w-full text-center px-4 py-3 text-base font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-all"
              @click="showMobileMenu = false"
            >
              <i class="pi pi-question-circle mr-2"></i>
              How It Works
            </router-link>
            <router-link
              to="/auth/register"
              class="block w-full text-center px-4 py-3 text-base font-semibold text-white rounded-lg shadow-md transition-all hover:shadow-lg"
              :class="mobileGetStartedBtnClass"
              @click="showMobileMenu = false"
            >
              Get Started
            </router-link>
            <router-link
              to="/auth/login"
              class="block w-full text-center px-4 py-3 text-base font-medium text-gray-700 bg-white border-2 border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              @click="showMobileMenu = false"
            >
              Sign In
            </router-link>
          </div>
        </template>

        <!-- Authenticated User Options -->
        <template v-else>
          <router-link
            v-if="!authStore.hasRole('Admin') && !authStore.hasRole('Provider') && !authStore.hasRole('ServiceUser')"
            to="/dashboard"
            class="mobile-nav-link"
            @click="showMobileMenu = false"
          >
            <i class="pi pi-chart-line mr-2"></i>
            Dashboard
          </router-link>

          <router-link
            v-if="authStore.hasPermission('appointments.read') && !authStore.hasRole('Provider') && !authStore.hasRole('Admin')"
            to="/appointments"
            class="mobile-nav-link"
            @click="showMobileMenu = false"
          >
            <i class="pi pi-calendar mr-2"></i>
            Appointments
          </router-link>

          <router-link
            v-if="authStore.hasRole('Provider')"
            to="/provider"
            class="mobile-nav-link mobile-provider-hub-link"
            @click="showMobileMenu = false"
          >
            <i class="pi pi-briefcase mr-2"></i>
            Provider Hub
          </router-link>

          <router-link
            v-if="authStore.hasRole('ShopStaff')"
            to="/shop/dashboard"
            class="mobile-nav-link"
            @click="showMobileMenu = false"
          >
            <i class="pi pi-shop mr-2"></i>
            Shop Dashboard
          </router-link>

          <router-link
            v-if="authStore.hasRole('ServiceUser')"
            to="/service-user/dashboard"
            class="mobile-nav-link"
            @click="showMobileMenu = false"
          >
            <i class="pi pi-th-large mr-2"></i>
            My Services
          </router-link>
        </template>

        <!-- Email verification alert moved outside header -->
      </div>
    </div>
  </nav>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { useAuthStore } from '../stores/auth'
import { useServiceStore } from '../stores/service'
import { useServicesStore } from '../stores/services'
import { useRouter, useRoute } from 'vue-router'
import { useBranding } from '@/composables/useBranding'

// Initialize stores
const authStore = useAuthStore()
const serviceStore = useServiceStore()
const servicesStore = useServicesStore()
const router = useRouter()
const route = useRoute()

// Branding
const { brand } = useBranding()

// Detect if we're in services mode (for Zaveato)
const isServicesMode = computed(() => {
  // Check query param
  if (route.query.mode === 'services') return true
  // Check if on services-related routes
  const servicesRoutes = ['/services', '/booking', '/appointments']
  return servicesRoutes.some(r => route.path.startsWith(r))
})

// State
const showUserMenu = ref(false)
const showMobileMenu = ref(false)
const showGuidesDropdown = ref(false)
const guidesDropdownRef = ref<HTMLElement | null>(null)

// Watch route changes to close mobile menu
watch(() => route.path, () => {
  showMobileMenu.value = false
  showGuidesDropdown.value = false
})

// Nav bar classes - Zaveato dark header
const navClasses = computed(() => {
  return 'bg-gray-900 border-b border-gray-800'
})

// Get Started button classes - Zaveato amber accent
const getStartedBtnClass = computed(() => {
  return 'text-amber-600 hover:text-amber-700'
})

// Mobile Get Started button classes - Zaveato dark theme with amber accent
const mobileGetStartedBtnClass = computed(() => {
  return 'bg-gray-900 hover:bg-gray-800 border border-amber-500'
})

// Computed
const logoLink = computed(() => {
  // For Zaveato, go to unified home with Shop/Book toggle
  return '/'
})

// Methods
function toggleUserMenu() {
  showUserMenu.value = !showUserMenu.value
  showMobileMenu.value = false
}

function toggleMobileMenu() {
  showMobileMenu.value = !showMobileMenu.value
  showUserMenu.value = false
}

async function handleLogout() {
  showUserMenu.value = false
  try {
    await authStore.logout()
  } catch (error) {
    console.error('Logout error in navigation:', error)
    // Force navigation to login even if logout fails
    await router.push('/auth/login')
  }
}

// Reset search filters when navigating to home or services
function handleResetSearch() {
  // Clear both stores to ensure all search states are reset
  serviceStore.resetSearch() // For /services page
  servicesStore.clearFilters() // For home page
}


// Close menus when clicking outside
function handleClickOutside(event: Event) {
  const target = event.target as HTMLElement
  // Close user menu if clicking outside
  if (!target.closest('.relative')) {
    showUserMenu.value = false
    showMobileMenu.value = false
  }
  // Close guides dropdown if clicking outside its container
  if (guidesDropdownRef.value && !guidesDropdownRef.value.contains(target)) {
    showGuidesDropdown.value = false
  }
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
})
</script>

<style scoped>
.nav-link {
  @apply inline-flex items-center px-3 py-2 rounded-md text-sm font-medium text-white/80 hover:text-white hover:bg-white/10 transition-colors duration-200;
}

.nav-link-active {
  @apply bg-white/20 text-white;
}

.provider-hub-link {
  background: #000;
  color: #fff !important;
  border: 1px solid rgba(255,255,255,0.15);
}
.provider-hub-link:hover {
  background: #1f1f1f !important;
  color: #fff !important;
}
.provider-hub-link-active {
  background: #111 !important;
  color: #fff !important;
}

.mobile-provider-hub-link {
  background: #000 !important;
  color: #fff !important;
  font-weight: 600;
}
.mobile-provider-hub-link:hover {
  background: #1f1f1f !important;
  color: #fff !important;
}

.admin-link {
  @apply bg-white/20 border border-white/30 rounded-md px-3 py-2 text-white hover:bg-white/30 hover:border-white/40;
}

.admin-link.nav-link-active {
  @apply bg-white/30 border-white/40 text-white;
}

.mobile-nav-link {
  @apply block px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-200 transition-colors duration-200;
}

/* Responsive Navigation Styles */

/* Navigation Container - matches StorefrontLayout px-4 lg:px-6 */
.nav-container {
  width: 100%;
  margin: 0 auto;
  padding: 0 1rem; /* px-4 */
}

@media (min-width: 1024px) {
  .nav-container {
    padding: 0 1.5rem; /* lg:px-6 */
  }
}

.nav-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 4rem;
}

@media (min-width: 768px) {
  .nav-content {
    height: 4.5rem;
  }
}

.nav-links {
  display: none;
}

.nav-actions {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.mobile-menu-btn {
  @apply p-2 text-white/90 hover:text-white hover:bg-white/10 rounded-md;
  display: block;
}

.mobile-nav {
  display: block;
}

.mobile-nav-content {
  @apply px-2 pt-2 pb-3 space-y-1 bg-gray-50;
}

/* Small Mobile (480px and up) - additional rules */
@media (min-width: 480px) {
  .nav-actions {
    gap: 0.75rem;
  }
}

/* Large Tablet/Small Desktop (1024px and up) - Show desktop nav */
@media (min-width: 1024px) {
  .nav-container {
    max-width: 100%;
    padding: 0 1.5rem;
  }

  .nav-links {
    display: flex;
    align-items: center;
    gap: 1.5rem;
    margin-left: 2rem;
    font-size: 0.9375rem;
  }

  .nav-actions {
    gap: 0.875rem;
  }

  .mobile-menu-btn {
    display: none;
  }

  .mobile-nav {
    display: none;
  }
}

/* Medium Desktop (1150px and up) */
@media (min-width: 1150px) {
  .nav-container {
    padding: 0 2rem;
  }

  .nav-links {
    gap: 1.75rem;
    margin-left: 2.5rem;
  }

  .nav-actions {
    gap: 1rem;
  }
}

/* Desktop (1280px and up) */
@media (min-width: 1280px) {
  .nav-container {
    max-width: 1400px;
  }

  .nav-links {
    gap: 2rem;
    margin-left: 2.5rem;
    font-size: 1rem;
  }

  .nav-actions {
    gap: 1.25rem;
  }
}

/* Large Desktop (1536px and up) */
@media (min-width: 1536px) {
  .nav-container {
    max-width: 1536px;
  }

  .nav-links {
    gap: 2.5rem;
    margin-left: 3rem;
  }
}

/* Extra Large Desktop (1920px and up) */
@media (min-width: 1920px) {
  .nav-container {
    max-width: 1800px;
  }

  .nav-links {
    gap: 3rem;
  }
}

/* Dropdown transition */
.dropdown-enter-active,
.dropdown-leave-active {
  transition: all 0.2s ease;
}

.dropdown-enter-from,
.dropdown-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}
</style>